@file:UseSerializers(UuidSerializer::class)

package it.neckar.lizergy.model.configuration.quote.builder

import it.neckar.lizergy.lifeCycle.LizergyHasLifeCycle
import it.neckar.lizergy.lifeCycle.LizergyLifeCycleState
import it.neckar.lizergy.model.configuration.quote.builder.InverterType.InverterId
import it.neckar.lizergy.model.configuration.quote.builder.InverterType.MppInput
import it.neckar.lizergy.model.energy.Capacity
import it.neckar.open.kotlin.lang.fastFor
import it.neckar.uuid.HasUuid
import it.neckar.uuid.UuidSerializer
import it.neckar.uuid.randomForEnvironment
import kotlinx.serialization.Serializable
import kotlinx.serialization.UseSerializers
import kotlin.uuid.Uuid

@Serializable
sealed interface InverterType : HasUuid, LizergyHasLifeCycle {
  val id: InverterId

  val description: String

  val dataSheetPath: String?

  val manufacturer: Manufacturer

  val phase: Phase

  override val uuid: Uuid
    get() = id.uuid

  @Serializable
  enum class Phase {
    SinglePhase,
    ThreePhase,

    ;

    override fun toString(): String {
      return when (this) {
        SinglePhase -> "einphasig"
        ThreePhase -> "dreiphasig"
      }
    }
  }

  @Serializable
  data class MppInput(val inputIndex: Int, val strings: List<StringMppInput>) {
    fun format(): String {
      return buildString {
        append("MPP-Eingang ${inputIndex + 1} (${'A' + inputIndex}): ")
        append("${strings.size} ${if (strings.size == 1) "String" else "Strings"}")
      }
    }
  }

  @Serializable
  data class StringMppInput(val stringIndex: Int)

  fun format(): String

  fun formatForQuote(suffix: String = "er"): String

  /**
   * An id for an inverter type
   */
  @Serializable
  data class InverterId(val uuid: Uuid) {

    override fun toString(): String {
      return uuid.toString()
    }

    fun format(): String {
      return uuid.toString()
    }

    companion object {
      fun random(): InverterId {
        return InverterId(Uuid.randomForEnvironment())
      }
    }
  }
}

@Serializable
sealed interface InverterWithStringInputs : InverterType {
  val capacity: Capacity

  /**
   * Maximum power point tracking
   */
  val mppInputs: List<MppInput>

  val numberOfMppInputs: Int
    get() = mppInputs.size

  val totalNumberOfStrings: Int
    get() = mppInputs.sumOf { it.strings.size }
}

@Serializable
sealed interface BatteryInverter : InverterType {
  val dischargeCapacity: Capacity
}


@Serializable
data class Inverter(
  override val id: InverterId,
  override val description: String,
  override val manufacturer: Manufacturer,
  override val capacity: Capacity,
  override val phase: InverterType.Phase,
  /**
   * Maximum power point tracking
   */
  override val mppInputs: List<MppInput>,
  override val dataSheetPath: String? = null,
  override val lifeCycleState: LizergyLifeCycleState = LizergyLifeCycleState.Active,
) : InverterWithStringInputs {

  override fun format(): String {
    return buildString {
      append(description)
      append(", Leistung: ${capacity.format()}")
      append(", $phase")
    }
  }

  override fun formatForQuote(suffix: String): String {
    return buildString {
      append("${phase}$suffix Wechselrichter")
      append(" ")
      append(description)
    }
  }

  companion object {
    operator fun invoke(
      id: InverterId,
      description: String,
      manufacturer: Manufacturer,
      capacity: Capacity,
      phase: InverterType.Phase,
      numberOfStringsForMppTrackerInput: List<Int>,
      dataSheetPath: String? = null,
      lifeCycleState: LizergyLifeCycleState = LizergyLifeCycleState.Active,
    ): Inverter {
      val mppInputs = numberOfStringsForMppTrackerInput.mapIndexed { index, numberOfStrings ->
        val strings = buildList { numberOfStrings.fastFor { index -> add(InverterType.StringMppInput(index)) } }
        MppInput(index, strings)
      }
      return Inverter(id, description, manufacturer, capacity, phase, mppInputs, dataSheetPath, lifeCycleState)
    }
  }
}

@Serializable
data class HybridInverter(
  override val id: InverterId,
  override val description: String,
  override val manufacturer: Manufacturer,
  override val capacity: Capacity,
  override val dischargeCapacity: Capacity,
  override val phase: InverterType.Phase,
  /**
   * Maximum power point tracking
   */
  override val mppInputs: List<MppInput>,
  override val dataSheetPath: String? = null,
  override val lifeCycleState: LizergyLifeCycleState = LizergyLifeCycleState.Active,
) : BatteryInverter, InverterWithStringInputs {

  override fun format(): String {
    return buildString {
      append(description)
      append(", Leistung: ${capacity.format()}")
      dischargeCapacity.let { append(", Entladeleistung: ${it.format()}") }
      append(", $phase")
      if (mppInputs.isNotEmpty()) {
        append(", MPP-Eingänge: ${mppInputs.joinToString { it.format() }}")
      }
    }
  }

  override fun formatForQuote(suffix: String): String {
    return buildString {
      append("${phase}$suffix Hybridwechselrichter")
      append(" ")
      append(description)
    }
  }

  fun getShortType(): String {
    return if (description.startsWith("Sunny Tripower")) "STP Smart Energy"
    else if (description.startsWith("Sunny Boy")) "SB Smart Energy"
    else if (description.startsWith("IBC")) "IBC ${description.split(" ").last()} Serie"
    else throw IllegalStateException("Unknown inverter short type: $description")
  }

  companion object {
    operator fun invoke(
      id: InverterId,
      description: String,
      manufacturer: Manufacturer,
      capacity: Capacity,
      dischargeCapacity: Capacity,
      phase: InverterType.Phase,
      numberOfStringsForMppTrackerInput: List<Int>,
      dataSheetPath: String? = null,
      lifeCycleState: LizergyLifeCycleState = LizergyLifeCycleState.Active,
    ): HybridInverter {
      val mppInputs = numberOfStringsForMppTrackerInput.mapIndexed { index, numberOfStrings ->
        val strings = buildList { numberOfStrings.fastFor { index -> add(InverterType.StringMppInput(index)) } }
        MppInput(index, strings)
      }
      return HybridInverter(id, description, manufacturer, capacity, dischargeCapacity, phase, mppInputs, dataSheetPath, lifeCycleState)
    }
  }
}

@Serializable
data class BasicBatteryInverter(
  override val id: InverterId,
  override val description: String,
  override val manufacturer: Manufacturer,
  override val dischargeCapacity: Capacity,
  override val phase: InverterType.Phase,
  override val dataSheetPath: String? = null,
  override val lifeCycleState: LizergyLifeCycleState = LizergyLifeCycleState.Active,
) : BatteryInverter {
  override fun format(): String {
    return buildString {
      append(description)
      append(", Entladeleistung: ${dischargeCapacity.format()}")
      append(", $phase")
    }
  }

  override fun formatForQuote(suffix: String): String {
    return buildString {
      append("${phase}$suffix Batteriewechselrichter")
      append(" ")
      append(description)
    }
  }
}
